/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/build/QaNewChecklistDialog.tsx
 * FILE_ID: JOBHUB-QA-NEW-CHECKLIST-DIALOG-001
 * PURPOSE: Draft a checklist in place; write nothing until Save.
 * LANDMINE: In-flow panel, max-h-[45vh], never absolute/fixed/z-indexed.
 *      Cancel must leave no rows. Save writes sequentially so numbers stay ordered.
 * LANDMINE: `fill` (phone sheet) swaps the 45vh cap for the sheet's height and
 *      scrolls only the rows, so title and Save stay on screen.
 */
import { useEffect, useRef } from 'react'
import { LayoutGrid, Loader2, Plus, Table2, X } from 'lucide-react'
import { cn } from '@quotech/tenant-shared/lib/utils'
import type { QaApi } from '../../../qa/qaApi'
import type { JobHubHttpClient } from '../../../jobHubDataAdapter'
import { QaNewChecklistDraftCards } from './QaNewChecklistDraftCards'
import { QaNewChecklistDraftTable } from './QaNewChecklistDraftTable'
import { useQaNewChecklistDraft } from './useQaNewChecklistDraft'

export type QaNewChecklistLayout = 'table' | 'cards'

export function QaNewChecklistDialog({
  areaId,
  api,
  onCreated,
  onMutated,
  onCancel,
  layout = 'table',
  fill = false,
  onToggleLayout,
  client,
}: {
  areaId: string
  api: QaApi
  onCreated: (checklistId: string) => void
  onMutated: () => void
  onCancel: () => void
  layout?: QaNewChecklistLayout
  fill?: boolean
  /** Absent -> no layout toggle (desktop). */
  onToggleLayout?: () => void
  client?: JobHubHttpClient
}) {
  const draft = useQaNewChecklistDraft({ areaId, api, onCreated, onMutated })
  const titleRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => { titleRef.current?.focus() }, [])
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])

  const addRow = () => {
    draft.addRow()
    if (layout !== 'cards') return
    requestAnimationFrame(() => {
      const cards = rootRef.current?.querySelectorAll('[data-draft-row]')
      cards?.[cards.length - 1]?.scrollIntoView?.({ block: 'nearest' })
    })
  }

  const rowProps = {
    rows: draft.rows,
    onPatch: draft.patch,
    onMove: draft.move,
    onRemove: draft.removeRow,
  }
  const body = layout === 'cards'
    ? <QaNewChecklistDraftCards {...rowProps} client={client} />
    : <QaNewChecklistDraftTable {...rowProps} fill={fill} />

  return (
    <div
      ref={rootRef}
      className={fill
        ? 'flex h-full min-h-0 flex-col'
        : 'mb-2 max-h-[45vh] overflow-auto rounded-lg border border-border bg-card p-2'}
      data-testid="qa-new-checklist-dialog"
      data-layout={layout}
    >
      <div className={cn('mb-2 flex items-center gap-2', fill && 'flex-none')}>
        <input
          ref={titleRef}
          value={draft.title}
          onChange={(event) => draft.setTitle(event.target.value)}
          aria-label="New checklist title"
          className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm font-semibold"
        />
        {onToggleLayout ? (
          <button
            type="button"
            onClick={onToggleLayout}
            data-testid="qa-new-checklist-layout-toggle"
            aria-label={layout === 'cards' ? 'Switch to table view' : 'Switch to card view'}
            title={layout === 'cards' ? 'Table view' : 'Card view'}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
          >
            {layout === 'cards'
              ? <Table2 className="h-4 w-4" aria-hidden />
              : <LayoutGrid className="h-4 w-4" aria-hidden />}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel new checklist"
          className="rounded p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {draft.error && <p role="alert" className="mb-2 text-xs text-destructive">{draft.error}</p>}
      {fill ? <div className="min-h-0 flex-1 overflow-auto" data-testid="qa-new-checklist-scroller">{body}</div> : body}
      <div className={cn('mt-2 flex flex-wrap items-center gap-2', fill && 'flex-none')}>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-xs text-muted-foreground"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add row
        </button>
        <button
          type="button"
          disabled={draft.busy}
          onClick={() => void draft.save()}
          data-testid="qa-new-checklist-save"
          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium"
        >
          {draft.busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="qa-new-checklist-cancel"
          className="text-xs text-muted-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
